import { useState } from 'react';
import { useCancelUserDeletion } from '../hooks/useAdminOps';
import {
  CANCEL_DOES_NOT_REINSTATE_TEXT,
  NOTE_MAX_LENGTH,
  noteBlocker,
} from '../pages/userDeletion.logic';

interface CancelDeletionActionProps {
  userId: string;
  /** Keeps field ids unique: the queue drawer and the enforcement page both render one. */
  idPrefix: string;
  /** Extra reason to hold the button, e.g. the deletion state is being re-read. */
  disabled?: boolean;
  onCancelled?: () => void;
}

/**
 * DM-7185: cancel an account's scheduled deletion, with the required note.
 *
 * One component for both surfaces so the rule that matters -- a note is always
 * recorded, and the operator is always told that cancelling does not reinstate
 * a suspended account -- cannot drift between them. Callers decide WHETHER to
 * render it (the deletion is active, Phase 4 has not started, the role may
 * write); this only decides whether the form is ready to submit.
 */
export default function CancelDeletionAction({
  userId,
  idPrefix,
  disabled,
  onCancelled,
}: CancelDeletionActionProps) {
  const [note, setNote] = useState('');
  const cancelDeletion = useCancelUserDeletion();

  const blocker = noteBlocker(note);
  const noteId = `${idPrefix}-cancel-note`;
  const helpId = `${idPrefix}-cancel-help`;

  const submit = () => {
    if (blocker !== null || disabled || cancelDeletion.isPending) return;
    cancelDeletion.mutate(
      { userId, note: note.trim() },
      {
        onSuccess: () => {
          setNote('');
          onCancelled?.();
        },
      }
    );
  };

  return (
    <div className="space-y-2">
      <label className="label" htmlFor={noteId}>
        Why the deletion is being cancelled (required)
      </label>
      <textarea
        id={noteId}
        className="input w-full min-h-[80px]"
        maxLength={NOTE_MAX_LENGTH}
        required
        aria-describedby={helpId}
        placeholder="Kept with the deletion record. Do not paste the donor’s email or message here."
        value={note}
        onChange={event => setNote(event.target.value)}
      />
      <p className="text-xs text-gray-text text-right tabular-nums">
        {note.length}/{NOTE_MAX_LENGTH}
      </p>
      <button
        type="button"
        onClick={submit}
        disabled={blocker !== null || disabled || cancelDeletion.isPending}
        className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {cancelDeletion.isPending ? 'Cancelling…' : 'Cancel deletion'}
      </button>
      <p id={helpId} className="text-xs text-gray-text">
        {CANCEL_DOES_NOT_REINSTATE_TEXT} Takes effect immediately — no second operator is needed.
      </p>
    </div>
  );
}
